import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import FancyProgressArc from './FancyProgressArc'
import GenerateScoringButton from './buttons/GenerateScoringButton'
import { openScoreDetailsModal } from '../services/Modals'
import type { Question } from '../types/question'

interface ScoreArcProps {
  // The estimated score percent (0–100). Optional — defaults to
  // `question.bideval.scoreint` (with a `>= 0` guard so the app's
  // "-1 == not yet generated" convention still renders as N/A).
  // Pass explicitly to override.
  value?: number | null | undefined
  scale?: number
  tooltip?: string
  // Question (or temp question) whose bideval + answerstructure
  // drive the read-only score-breakdown modal opened by the default
  // click handler. When omitted, clicks do nothing unless an explicit
  // `onClick` is provided.
  question?: Question | null
  modalTitle?: string
  // Override the click behaviour entirely. When set, the default
  // modal-open handler is skipped - useful for callers that want
  // their own popout.
  onClick?: () => void
  // While true the arc swaps its centre label for a CircularProgress
  // spinner — callers no longer have to flip between a sibling
  // CircularProgress and the arc to show "scoring in progress".
  loading?: boolean
}

// Thin wrapper around FancyProgressArc preconfigured for the per-question
// estimated score. Renders a single arc for a single question — callers
// that want a "before → after" comparison strip use `ScoreArcPair` instead.
// Pass `question` to open the read-only score-details modal on click
// (rendered by the centralised `ModalHost`); pass `onClick` to take over
// the click behaviour entirely.
export default function ScoreArc({
  value, scale = 1, tooltip, question, modalTitle, onClick, loading,
}: ScoreArcProps) {
  // Resolve the arc value. Explicit `value` wins; otherwise derive from
  // `question.bideval.scoreint`. `-1` (and other negatives) fall back
  // to `undefined` so the arc renders as N/A — matches the codebase's
  // "-1 == not yet generated" convention.
  const derivedScoreint = (question as any)?.bideval?.scoreint
  const resolvedValue: number | null | undefined = value !== undefined
    ? value
    : (typeof derivedScoreint === 'number' && derivedScoreint >= 0 ? derivedScoreint : undefined)
  const defaultClick = question
    ? () => openScoreDetailsModal({
      question,
      title: modalTitle ?? tooltip,
    })
    : undefined
  // When the arc is in the N/A state AND we're rendering the
  // GenerateScoringButton as the centre slot, the arc-level click is
  // suppressed — the user's click should land on the button (which
  // fires its own runScore pipeline) rather than open the
  // score-details modal against a question with no bideval.
  const isNA = resolvedValue == null
  const showGenerateButton = isNA && !!question
  const effectiveClick = showGenerateButton ? undefined : (onClick ?? defaultClick)
  return (
    <FancyProgressArc
      value={resolvedValue}
      scale={scale}
      icon={<AssessmentOutlinedIcon sx={{ fontSize: 14, opacity: 0.6 }} />}
      tooltip={tooltip ?? ''}
      loading={loading}
      naSlot={question ? <GenerateScoringButton question={question} iconSize={Math.round(16 * scale)} /> : undefined}
      {...(effectiveClick ? { onClick: effectiveClick, style: { cursor: 'pointer' } } : null)}
    />
  )
}
